import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
  limit,
} from "firebase/firestore";
import { db } from "./firebase";

export interface AccessCheckResult {
  hasAccess: boolean;
  reason:
    | "super_admin"
    | "org_member"
    | "access_granted"
    | "pending_invitation"
    | "domain_auto_join"
    | "no_access";
  details?: string;
}

/**
 * Normalize email for consistent lookups
 */
export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

/**
 * Check if a user has a pending invitation to any org
 */
export async function checkPendingInvitation(
  email: string
): Promise<{ hasPending: boolean; orgName?: string }> {
  const normalizedEmail = normalizeEmail(email);

  const invitationsQuery = query(
    collection(db, "invitations"),
    where("email", "==", normalizedEmail),
    where("status", "==", "pending"),
    limit(1)
  );

  const snapshot = await getDocs(invitationsQuery);

  if (snapshot.empty) {
    return { hasPending: false };
  }

  const invitation = snapshot.docs[0].data();
  return { hasPending: true, orgName: invitation.orgName };
}

/**
 * Check if an email domain matches any org's domainAutoJoin setting
 */
export async function checkDomainAutoJoin(
  email: string
): Promise<{ matches: boolean; orgNames?: string[] }> {
  const domain = email.split("@")[1]?.toLowerCase();
  if (!domain) {
    return { matches: false };
  }

  const orgsQuery = query(
    collection(db, "orgs"),
    where("domainAutoJoin", "==", domain)
  );

  const snapshot = await getDocs(orgsQuery);

  if (snapshot.empty) {
    return { matches: false };
  }

  const orgNames = snapshot.docs.map((doc) => doc.data().name as string);
  return { matches: true, orgNames };
}

/**
 * Check if an email is pre-approved for access
 */
export async function checkApprovedEmail(
  email: string
): Promise<{ isApproved: boolean; approvedAt?: Date }> {
  const normalizedEmail = normalizeEmail(email);
  const approvedRef = doc(db, "approvedEmails", normalizedEmail);
  const approvedDoc = await getDoc(approvedRef);

  if (!approvedDoc.exists()) {
    return { isApproved: false };
  }

  const data = approvedDoc.data();
  return {
    isApproved: true,
    approvedAt: data.approvedAt?.toDate(),
  };
}

/**
 * Check if a user is a super admin
 */
export async function checkSuperAdminAccess(userId: string): Promise<boolean> {
  const superAdminRef = doc(db, "superAdmins", userId);
  const superAdminDoc = await getDoc(superAdminRef);
  return superAdminDoc.exists();
}

/**
 * Central access check - determines if a user can use the app
 * Authenticated users get instant access (waitlist system removed)
 */
export async function checkAppAccess(
  userId: string,
  email: string | null,
  orgCount: number,
  isSuperAdmin: boolean
): Promise<AccessCheckResult> {
  const logPrefix = "[Access Check]";

  // All authenticated users have instant access (waitlist system removed)
  return { hasAccess: true, reason: "access_granted" };
}

/**
 * Grant access to a user by email
 * Used by admin to approve waitlist entries or add emails directly
 */
export async function grantAccessByEmail(
  email: string,
  grantedBy: string,
  note?: string
): Promise<void> {
  const normalizedEmail = normalizeEmail(email);

  // Add to approved emails collection
  const approvedRef = doc(db, "approvedEmails", normalizedEmail);
  await setDoc(approvedRef, {
    email: normalizedEmail,
    approvedAt: serverTimestamp(),
    approvedBy: grantedBy,
    note: note || null,
  });

  // If user already exists, also set flag on their user doc
  const usersQuery = query(
    collection(db, "users"),
    where("email", "==", normalizedEmail),
    limit(1)
  );
  const userSnapshot = await getDocs(usersQuery);

  if (!userSnapshot.empty) {
    const userDoc = userSnapshot.docs[0];
    await updateDoc(doc(db, "users", userDoc.id), {
      hasAppAccess: true,
      accessGrantedAt: serverTimestamp(),
      accessGrantedBy: grantedBy,
    });
  }

  // Update waitlist entry if exists
  const waitlistQuery = query(
    collection(db, "waitlist"),
    where("email", "==", normalizedEmail),
    limit(1)
  );
  const waitlistSnapshot = await getDocs(waitlistQuery);

  if (!waitlistSnapshot.empty) {
    const waitlistDoc = waitlistSnapshot.docs[0];
    await updateDoc(doc(db, "waitlist", waitlistDoc.id), {
      accessGranted: true,
      accessGrantedAt: serverTimestamp(),
      accessGrantedBy: grantedBy,
    });
  }
}

/**
 * Revoke access from a user by email
 */
export async function revokeAccessByEmail(
  email: string,
  revokedBy: string
): Promise<void> {
  const normalizedEmail = normalizeEmail(email);

  // Remove from approved emails
  const approvedRef = doc(db, "approvedEmails", normalizedEmail);
  const approvedDoc = await getDoc(approvedRef);
  if (approvedDoc.exists()) {
    await updateDoc(approvedRef, {
      revoked: true,
      revokedAt: serverTimestamp(),
      revokedBy,
    });
  }

  // Update user doc if exists
  const usersQuery = query(
    collection(db, "users"),
    where("email", "==", normalizedEmail),
    limit(1)
  );
  const userSnapshot = await getDocs(usersQuery);

  if (!userSnapshot.empty) {
    const userDoc = userSnapshot.docs[0];
    await updateDoc(doc(db, "users", userDoc.id), {
      hasAppAccess: false,
      accessRevokedAt: serverTimestamp(),
      accessRevokedBy: revokedBy,
    });
  }
}

/**
 * Debug function: Check why an email would or wouldn't have access
 * Returns detailed information for admin debugging
 */
export async function debugAccessCheck(email: string): Promise<{
  email: string;
  normalizedEmail: string;
  wouldHaveAccess: boolean;
  checks: {
    name: string;
    passed: boolean;
    details?: string;
  }[];
}> {
  const normalizedEmail = normalizeEmail(email);
  const checks: { name: string; passed: boolean; details?: string }[] = [];

  // Check approved emails
  const approvedCheck = await checkApprovedEmail(normalizedEmail);
  checks.push({
    name: "Pre-approved email",
    passed: approvedCheck.isApproved,
    details: approvedCheck.isApproved
      ? `Approved at ${approvedCheck.approvedAt?.toISOString()}`
      : "Not in approved list",
  });

  // Check pending invitations
  const invitationCheck = await checkPendingInvitation(normalizedEmail);
  checks.push({
    name: "Pending invitation",
    passed: invitationCheck.hasPending,
    details: invitationCheck.hasPending
      ? `Invited to ${invitationCheck.orgName}`
      : "No pending invitations",
  });

  // Check domain auto-join
  const domainCheck = await checkDomainAutoJoin(normalizedEmail);
  checks.push({
    name: "Domain auto-join",
    passed: domainCheck.matches,
    details: domainCheck.matches
      ? `Can join: ${domainCheck.orgNames?.join(", ")}`
      : `Domain ${normalizedEmail.split("@")[1]} not configured for auto-join`,
  });

  // Check if user exists with access flag
  const usersQuery = query(
    collection(db, "users"),
    where("email", "==", normalizedEmail),
    limit(1)
  );
  const userSnapshot = await getDocs(usersQuery);

  if (!userSnapshot.empty) {
    const userData = userSnapshot.docs[0].data();
    const hasFlag = userData.hasAppAccess === true;
    const orgCount = 0; // Would need to check memberships

    checks.push({
      name: "User exists",
      passed: true,
      details: `User ID: ${userSnapshot.docs[0].id}`,
    });

    checks.push({
      name: "hasAppAccess flag",
      passed: hasFlag,
      details: hasFlag ? "Access granted via flag" : "Flag not set or false",
    });
  } else {
    checks.push({
      name: "User exists",
      passed: false,
      details: "User has not signed in yet",
    });
  }

  // Check waitlist status
  const waitlistQuery = query(
    collection(db, "waitlist"),
    where("email", "==", normalizedEmail),
    limit(1)
  );
  const waitlistSnapshot = await getDocs(waitlistQuery);

  if (!waitlistSnapshot.empty) {
    const waitlistData = waitlistSnapshot.docs[0].data();
    checks.push({
      name: "On waitlist",
      passed: true,
      details: waitlistData.accessGranted
        ? "Waitlist approved"
        : "Waitlist pending",
    });
  } else {
    checks.push({
      name: "On waitlist",
      passed: false,
      details: "Not on waitlist",
    });
  }

  const wouldHaveAccess = checks.some(
    (c) =>
      c.passed &&
      [
        "Pre-approved email",
        "Pending invitation",
        "Domain auto-join",
        "hasAppAccess flag",
      ].includes(c.name)
  );

  return {
    email,
    normalizedEmail,
    wouldHaveAccess,
    checks,
  };
}
